import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { IPlugin, ICreatePlugin } from './plugin.interface'
import { HttpErrorHandlerService } from '../helpers/http-error-handler.service'
import { IUpdateDTModel } from '../helpers/update-model.interface'
import { BehaviorSubject, catchError, filter, finalize, firstValueFrom, Subject } from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'
import { API_ENDPOINT_PATH, API_HTTP_HEADERS } from "../helpers/api-constant";

@Injectable({
  providedIn: 'root'
})
export class PluginService {
  readonly #headers: HttpHeaders
  readonly #urlEndpoint: string

  private readonly pluginSubject$: BehaviorSubject<{plugin: IPlugin, event: 'activate'|'update'}> = new BehaviorSubject<{plugin: IPlugin, event: 'activate'|'update'}>({
    plugin: {
      _id: '-1',
      enabled: false,
      name: '',
      version: ''
    },
    event: 'activate'
  })

  private readonly plugin$: Observable<{plugin: IPlugin, event: 'activate'|'update'}> = this.pluginSubject$.asObservable()
    .pipe(
      filter((data: {plugin: IPlugin, event: 'activate'|'update'})=> data.plugin._id !== '-1'),
      finalize(() => console.log('plugin$ - Observable completed.'))
    )

  private readonly pluginAdditionActionSubject$: Subject<{
    pluginId: string,
    pluginModule: string
    executeAction: string
    data: any
  }> = new Subject()
  private readonly pluginAdditionAction$ = this.pluginAdditionActionSubject$.asObservable()

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandlerService: HttpErrorHandlerService
  ) {
	  this.#urlEndpoint = `${API_ENDPOINT_PATH}/plugin`;
	  this.#headers = new HttpHeaders(API_HTTP_HEADERS);
  }

  private getUrl(): string {
    return `${this.#urlEndpoint}`
  }

  private getHeader(): HttpHeaders {
    return this.#headers
  }

  notifyPlugin(plugin: IPlugin, event: 'activate'|'update'): void {
    this.pluginSubject$.next({plugin: plugin, event: event})
  }

  observePlugin$(): Observable<{plugin: IPlugin, event: 'activate'|'update'}> {
    return this.plugin$
  }

  executeAdditionalPluginAction$(pluginId: string, pluginModule: string, executeAction: string, data: any): void {
    this.pluginAdditionActionSubject$.next({pluginId: pluginId, pluginModule: pluginModule, executeAction: executeAction, data: data})
  }

  observeAdditionalPluginAction$():  Observable<{ pluginId: string; pluginModule: string; executeAction: string; data: any}> {
    return this.pluginAdditionAction$
  }

  async upsertOne(payload: IPlugin | ICreatePlugin): Promise<IPlugin|IUpdateDTModel|{}> {
    const result = this.http.post<IPlugin>(`${this.getUrl()}`, payload,
      { headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForSingleRecord))
    return await firstValueFrom(result)
  }

  async getById(id: string, attr?: string): Promise<IPlugin|{}> {
    let queryUrl = `${this.getUrl()}/${id}`
    if (attr) { queryUrl += `?attr=${attr}`}

    const result = this.http.get<IPlugin>(`${queryUrl}`,
      { headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForSingleRecord))
    return await firstValueFrom(result)
  }

  async listAll(params?: string): Promise<IPlugin[]> {
    let queryUrl = `${this.getUrl()}`
    if (params) { queryUrl += `?${params}` }

    const result = this.http.get<IPlugin[]>(`${queryUrl}`,
      { headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForRecords))
    return await firstValueFrom(result)
  }

  async deleteById(id: string): Promise<void> {
	  const headers = this.getHeader().delete('Content-Type').delete('content-type');

    const result = this.http.delete(`${this.getUrl()}/${id}`,
      { headers: headers }).pipe(catchError(this.errorHandlerService.handleError))
    await firstValueFrom(result)
  }
}
